import React from 'react';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { MetabolicVulnerabilityInsights } from './metabolic-vulnerability-insights';
import { MvxResultsPanel } from './mvx-results-panel';

/**
 * The MVX Plus section as it prints in the analysis PDF.
 *
 * Same shape as every other section's PDF block: a title, the Insights panel,
 * then the results. What it does NOT do is go through PDFSection, and the
 * reason is the charts. PDFSection renders a section's markers as the standard
 * reference-range bar; MVX draws two bespoke ones — MvxSeverityBar on a
 * normalized axis for the three index scores plus GlycA, and MvxOptimalBar for
 * the window markers — and the PDF is the client's copy of the screen, so it
 * has to carry the same bars the practitioner just looked at.
 *
 * So this renders the REAL panels rather than reproducing them. Both are
 * self-contained cards that already size to their content, which is also why
 * this does not need its own height estimate the way the generic path does.
 *
 * TWO FORCED PAGES, deliberately. The Insights panel is tall (gauge, pattern
 * bullets, the "Start with" line and the two sub-score explanations) and the
 * results are nine bar graphs. Measured together they overflow one page, and
 * an overflow here is the worst case: html2pdf slices through a bar graph and
 * the client gets half a chart. Opening the results on their own page costs a
 * little white space and removes that risk entirely. The caller supplies the
 * break that opens the Insights page, exactly as it does for Glucose.
 *
 * ⚠ The Training tab is NOT here and must never be. It is a paid-course pitch
 * and the PDF is the client's document. Same for the References, Range
 * References and Possible Root Causes tabs: practitioner reference material,
 * not something a client needs behind their results. Tabs are screen chrome.
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  /** The practitioner unticked "Include Insights" for this section. */
  hideInsightsPanel?: boolean;
}

export class MvxPdfSection extends React.Component<Props> {
  /** Nothing to print when the practitioner entered no MVX values. */
  static shouldRender(bloodworkAnalysis: BloodworkAnalysis | undefined): boolean {
    return MetabolicVulnerabilityInsights.shouldRender(bloodworkAnalysis);
  }

  render() {
    const { bloodworkAnalysis, hideInsightsPanel } = this.props;
    if (!MvxPdfSection.shouldRender(bloodworkAnalysis)) return null;

    return (
      <>
        <h4 className="pdf-section-title">
          <strong>Metabolic Vulnerability Index (MVX) Plus</strong>
        </h4>
        {!hideInsightsPanel && (
          <>
            <MetabolicVulnerabilityInsights bloodworkAnalysis={bloodworkAnalysis} />
            {/* NO TRAINING ATTRIBUTION HERE, deliberately. The screen carries
                "Dr. Darren Schmidt's ranges and method. Train with him →"
                under the panel, and this document does not.

                It is a departure from the usual rule that a UI change goes in
                the PDF too, and the reason is the AUDIENCE: the PDF is the
                CLIENT's copy, and whose ranges these are is a practitioner's
                concern. "That's for clients and they don't care" (Maryann,
                2026-10-16). The credit belongs where the practitioner reads
                it, on screen and on the Training tab. */}
            <div className="html2pdf__page-break" />
          </>
        )}
        <MvxResultsPanel bloodworkAnalysis={bloodworkAnalysis} />
      </>
    );
  }
}

export default MvxPdfSection;
